import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  NgZone,
  OnDestroy,
  afterNextRender,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { AppComponent } from '@app/app.component';
import { SmartWidgetShadowHostComponent } from '@app/smart-widget-shadow-host/smart-widget-shadow-host.component';
import { SMART_WIDGET_OVERLAY_CONTAINER_CLASS } from '@app/smart-widget-shadow-host/shadow-root-overlay-container';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { init } from '@sentry/angular';
import { CssOverwriteService } from '@services/feature/css-overwrite.service';
import { WidgetService } from '@services/feature/widget.service';
import { SwRouteService } from '@services/utils/sw-route.service';
import { environment } from '@environments/environment';
import { SentryConfig, SentryWebComponentConfig } from '../../sentry.config';

declare var __webpack_public_path__: string;

let sentryInitialized = false;

/**
 * Element attributes -> the widget's `Params` map: kebab-case -> snake_case, and
 * `custom-field-<id|identifier>` -> `custom_field[…]` (resolved downstream by
 * resolveCustomFieldIdentifiers). `class`/`style`/`id` and the render directives `shadow-dom` /
 * `light-dom` are skipped.
 */
export function elementAttributesToParams(el: HTMLElement): Params {
  const params: Params = {};
  for (const attr of el.getAttributeNames()) {
    if (attr === 'class' || attr === 'style' || attr === 'id' || attr === 'shadow-dom' || attr === 'light-dom') {
      continue;
    }
    const customFieldMatch = attr.match(/^custom-field-([\w-]+)$/);
    if (customFieldMatch) {
      params[`custom_field[${customFieldMatch[1]}]`] = el.getAttribute(attr) ?? '';
      continue;
    }
    params[attr.replace(/-/g, '_')] = el.getAttribute(attr) ?? '';
  }
  return params;
}

/**
 * Custom element wrapper for both delivery modes, selected by `iframe-mode` (set only by our own
 * index pages): present -> URL-driven iframe; absent -> Web Component, config from attributes.
 * A Web Component embed renders inside a shadow root by default; `light-dom` opts out of the
 * isolation and renders in the page's own DOM. Only one instance per page activates.
 */
@Component({
  selector: 'app-smart-widget-element',
  standalone: true,
  imports: [AppComponent, SmartWidgetShadowHostComponent],
  template: `
    @if (active()) {
      @if (isIframeMode || !shadowDomEnabled) {
        <app-root />
      } @else {
        <app-smart-widget-shadow-host />
      }
    } @else if (configError()) {
      <div class="sw-config-error" role="alert">{{ configError() }}</div>
    }
  `,
  styleUrl: './smart-widget-element.component.scss',
  // ⚠️ This wrapper stays in the LIGHT DOM — the shadow root belongs to the shadow-host component
  // (WC mode only); here it would also isolate the iframe variant.
  // ⚠️ NOT OnPush: tick() skips non-dirty OnPush views, so the Default-CD child would hang.
  changeDetection: ChangeDetectionStrategy.Default,
})
export class SmartWidgetElementComponent implements AfterViewInit, OnDestroy {

  private static activeInstance: SmartWidgetElementComponent | null = null;

  protected active = signal(false);
  protected configError = signal<string | null>(null);

  /** Live input: changing `locale` after init switches the widget language. */
  locale = input<string>();

  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private widgetService = inject(WidgetService);
  private cssOverwriteService = inject(CssOverwriteService);
  private translate = inject(TranslateService);
  private injector = inject(Injector);
  private ngZone = inject(NgZone);
  private rootOverlayContainer = inject(OverlayContainer);

  protected readonly isIframeMode = this.host.nativeElement.hasAttribute('iframe-mode');

  // Shadow DOM is the DEFAULT for a Web Component embed: the integrator page's CSS cannot reach the
  // widget and the widget's cannot reach the page. `light-dom` opts out - the escape hatch for the
  // documented survey-with-nested-booking case, which renders unstyled inside a shadow root. The
  // legacy `shadow-dom` attribute is still accepted and now means nothing extra.
  //
  // The payment step used to break in here: Stripe Elements and the PayPal SDK both resolve their
  // mount container through `document`, which cannot see into a shadow tree, so both failed
  // silently. Both now render into a light-DOM node projected back through a `<slot>` - see
  // LightDomProjectionDirective - which is what lets the boundary be on by default.
  protected readonly shadowDomEnabled = !this.host.nativeElement.hasAttribute('light-dom');

  constructor() {
    // Guarded to the active/first instance so a second element's locale attribute
    // cannot switch the first widget's language via the shared TranslateService.
    effect(() => {
      const loc = this.locale();
      const active = SmartWidgetElementComponent.activeInstance;
      if (loc && (active === null || active === this)) {
        this.translate.use(loc);
      }
    });
  }

  ngAfterViewInit(): void {
    if (SmartWidgetElementComponent.activeInstance && SmartWidgetElementComponent.activeInstance !== this) {
      console.error(
        `[SmartWidget] Only one <${environment.smartWidgetElementTag}> instance is supported ` +
        `per page in v1. The second instance will not render.`
      );
      return;
    }

    // Shared-state writes live here, AFTER the duplicate-instance guard, so a stray extra
    // element can never flip the active widget's mode. Still before the child <app-root>
    // exists — it is only created by active.set(true) below.
    this.widgetService.isWebComponentMode = !this.isIframeMode;
    if (!this.isIframeMode) {
      // In iframe mode AppComponent owns layoutType (parent BOOT message may set CHATBOX).
      this.widgetService.layoutType = 'IFRAME_URL';
      this.translate.setFallbackLang(environment.defaultLocale);
      // WC runs on the integrator's origin — point the chunk loader at ours, or the
      // intl-tel-input lazy chunk 404s. Iframe keeps the build default.
      __webpack_public_path__ = `${environment.deployUrl}webcomponent/`;
      // The WC build's document stylesheet is scoped to the widget-owned light-DOM nodes (see
      // extend.webpack.config.js), and the root CDK overlay container on `document.body` is one of
      // them in BOTH render modes - MatDialog always opens there, the datepicker does when the
      // shadow-root container has to decline. Marked here so a `light-dom` embed's overlays stay
      // styled too; the shadow host marks it again, harmlessly, for its own reasons.
      this.rootOverlayContainer.getContainerElement().classList.add(SMART_WIDGET_OVERLAY_CONTAINER_CLASS);
    }
    if (environment?.production && !sentryInitialized) {
      sentryInitialized = true;
      // WC variant must never capture the integrator page's errors/DOM (allowUrls-scoped,
      // no tracing/replay); iframe keeps the full config — it is our own page.
      init(this.isIframeMode ? SentryConfig : SentryWebComponentConfig);
    }

    if (this.isIframeMode) {
      // ⚠️ Required: initial navigation is bootstrap-listener-driven and never fires under
      // createApplication — without this kick, queryParams never emit the URL's params and
      // the widget silently boots with no config. afterNextRender ensures the child's
      // subscriptions exist first; ngZone.run keeps the config flow inside zone CD.
      SmartWidgetElementComponent.activeInstance = this;
      this.active.set(true);
      afterNextRender(
        () => {
          this.ngZone.run(() => {
            this.injector.get(Router).initialNavigation();
          });
        },
        { injector: this.injector }
      );
      return;
    }

    const el = this.host.nativeElement;
    const partnerUuid = el.getAttribute('partner-uuid');
    const widgetUuid = el.getAttribute('widget-uuid');

    if (!partnerUuid || !widgetUuid) {
      const message = `[SmartWidget] Missing required attributes: partner-uuid and widget-uuid.`;
      console.error(message);
      this.configError.set(message);
      return;
    }

    SmartWidgetElementComponent.activeInstance = this;

    const params = elementAttributesToParams(el);

    // Stripe 3DS returns to the HOST page URL, never an attribute.
    const hostQuery = new URLSearchParams(window.location.search);
    for (const key of ['payment_intent', 'payment_intent_client_secret', 'redirect_status']) {
      const value = hostQuery.get(key);
      if (value) {
        params[key] = value;
      }
    }

    const templateAttr = el.getAttribute('template');
    if (templateAttr) {
      this.cssOverwriteService.templateUuidFromAttributes = templateAttr;
    }

    // Render the child first: AppComponent's config-event subscription has no replay, so the
    // emit must happen after the child exists — and inside the zone, or CD never runs and the
    // widget hangs on the loading skeleton.
    this.active.set(true);
    afterNextRender(
      () => {
        this.ngZone.run(() => {
          this.injector.get(SwRouteService).initFromElementAttributes(params);
        });
      },
      { injector: this.injector }
    );
  }

  ngOnDestroy(): void {
    if (SmartWidgetElementComponent.activeInstance === this) {
      SmartWidgetElementComponent.activeInstance = null;
      this.cssOverwriteService.templateUuidFromAttributes = undefined;
    }
  }
}
